import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, useTheme } from '@mui/material';
import { AddFilled } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { PageHeader } from '@ui/layout/Layout/components';
import SSearchInput from '@ui/components/common/SSearchInput';
import { FiltersDiscardButton } from '@ui/components/common/buttons';
import { SDataGrid } from '@ui/components/common/grid/SDataGrid';
import { AssetDialog } from '@ui/components/Dialogs';
import { clearAssets, clearFilters, fetchAssets, setAddFilter, setPageNumber, setPageSize, setSearch } from '@application/redux/Assets/asset-slice';
import { setAssetId, setAssetName } from '@application/redux/SystemDetail/system-detail-slice';
import { formatDMY } from '@application/utilities/date-time-formater';
import useAssetsStyles from './useAssetsStyles';
import { ProjectApi } from '@application/api/endpoints/project-api';
const Assets = (props) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { assetsGridCardStyles, addAssetButtonStyles } = useAssetsStyles();
    const [isFilterEnabled, setIsFilterEnabled] = useState(false);
    const [gridFilteredColumns, setGridFilteredColumns] = useState([
        'noOfSystems',
        'endDate',
        'description'
    ]);
    const [projectQuery, setProjectQuery] = useState({
        pageNumber: 1,
        pageSize: 1000
    });
    const dispatch = useDispatch();
    const { assets, loading, error, search, pageNumber, pageSize, filteredColumns } = useSelector((state) => state.assetsDetail);
    const [projects, setProject] = useState();
    useEffect(() => {
        dispatch(fetchAssets());
        return () => {
            dispatch(clearAssets());
        };
    }, [dispatch, search, pageNumber, pageSize, filteredColumns]);
    useEffect(() => {
        return () => {
            dispatch(clearFilters());
        };
    }, [dispatch]);
    const [assetDialog, setAssetDialog] = useState({
        show: false,
        variant: '',
        title: ''
    });
    useEffect(() => {
        const getProjects = async () => {
            try {
                const response = await ProjectApi.getFilteredProjectDetail(projectQuery);
                if (response.isSuccess) {
                    setProject(response.data);
                }
            }
            catch (error) { }
        };
        getProjects();
    }, []);
    const handleEnableFilters = () => {
        setIsFilterEnabled(true);
    };
    const DiscardAndCloseFilters = () => {
        setIsFilterEnabled(false);
        dispatch(clearFilters());
    };
    const handleFilters = (filters) => {
        let startDate = filters.startDate;
        let endDate = filters.endDate;
        if (startDate) {
            startDate = dayjs(startDate).startOf('day').toDate();
        }
        if (endDate) {
            endDate = dayjs(endDate).endOf('day').toDate();
        }
        if (startDate) {
            setGridFilteredColumns(gridFilteredColumns.filter((item) => item != 'endDate'));
        }
        if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
            toast.error('End date cannot be before start date');
            return;
        }
        const filtersArray = Object.entries({ ...filters, startDate, endDate })
            .filter(([_, value]) => (typeof value === 'object' ? value !== null : value?.trim() !== ''))
            .map(([key, value]) => ({
            key,
            value: typeof value === 'object' && value?.toISOString ? value.toISOString() : value
        }));
        if (filtersArray.length > 0) {
            dispatch(setAddFilter(filtersArray));
        }
        else {
            dispatch(setAddFilter([]));
        }
    };
    const rows = assets?.data.data.map((a) => ({
        id: a.id || '',
        assetName: a.assetName || '',
        project: projects?.data?.find((p) => p.id == a.projectId)?.projectName,
        startDate: a.startDate,
        endDate: a.endDate,
        description: a.description || '',
        noOfSystems: a.noOfSystems || 0
    })) || [];
    const columns = [
        {
            field: 'assetName',
            headerName: 'Asset Name',
            minWidth: 150,
            flex: 1,
            editable: false,
            sortable: false
        },
        {
            field: 'project',
            headerName: 'Project',
            minWidth: 150,
            flex: 1,
            editable: false
        },
        {
            field: 'startDate',
            headerName: 'Start Date',
            minWidth: 150,
            type: 'date',
            flex: 1,
            editable: false,
            valueFormatter: (params) => formatDMY(params)
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            minWidth: 150,
            flex: 1,
            type: 'date',
            editable: false,
            valueFormatter: (params) => formatDMY(params)
        },
        {
            field: 'description',
            headerName: 'Description',
            minWidth: 300,
            flex: 1,
            editable: false
        },
        {
            field: 'noOfSystems',
            headerName: '# of Systems',
            type: 'number',
            align: 'left',
            headerAlign: 'left',
            minWidth: 180,
            flex: 1,
            editable: false
        }
    ];
    const handleOpenAssetDialog = () => {
        setAssetDialog((prev) => ({ ...prev, show: true, title: 'Create Asset', variant: 'add' }));
    };
    const handleCloseAssetDialog = () => {
        setAssetDialog((prev) => ({ ...prev, show: false, title: '', variant: '' }));
    };
    const handleRowClick = (params, event, details) => {
        dispatch(setAssetId(params.row.id));
        dispatch(setAssetName(params.row.assetName));
        navigate(`/detail-asset/${params.row.id}`);
    };
    const hasChanges = true;
    const hasNewChanges = isFilterEnabled && hasChanges;
    return (_jsxs(React.Fragment, { children: [_jsx(PageHeader, { title: 'Assets', icons: _jsx(_Fragment, { children: _jsx(Button, { size: 'small', variant: 'contained', color: 'primary', sx: addAssetButtonStyles, onClick: handleOpenAssetDialog, children: _jsx(AddFilled, { color: theme.palette.primary.contrastText, fontSize: 20 }) }) }), filters: _jsxs(_Fragment, { children: [_jsx(FiltersDiscardButton, { hasNewChanges: hasNewChanges, handleDiscardFilters: DiscardAndCloseFilters, handleShowFilters: handleEnableFilters }), _jsx(SSearchInput, { onChange: (e) => {
                                dispatch(setSearch(e.target.value));
                            } })] }) }), _jsx(Card, { className: 'assets-grid-card', sx: assetsGridCardStyles, children: _jsx(Box, { className: 'table-container', height: 'calc(100dvh - 240px)', children: _jsx(SDataGrid, { rows: rows, columns: columns, serverSidePagination: true, showFilters: isFilterEnabled, onRowClick: handleRowClick, totalRows: assets?.data.totalRecords, handleEnableFilters: handleEnableFilters, pageSizeOptions: [10, 20, 50, 100], onFilterChange: handleFilters, disabledColumns: gridFilteredColumns, loading: loading, initialState: {
                            pagination: {
                                paginationModel: {
                                    pageSize: assets?.data.pageSize || 10,
                                    page: assets?.data.pageNumber || 0
                                }
                            }
                        }, onPaginationModelChange: (model) => {
                            dispatch(setPageNumber(model.page + 1));
                            dispatch(setPageSize(model.pageSize));
                        }, filters: {}, setFilters: () => { } }) }) }), assetDialog.show && (_jsx(AssetDialog, { isOpen: assetDialog.show, onClose: handleCloseAssetDialog }))] }));
};
export default Assets;
